import type { ProjectPrefill } from '../api/prefillProjectFromDocuments';

type ProjectUploadStatus =
  | 'idle'
  | 'creating-project'
  | 'uploading'
  | 'analyzing'
  | 'done'
  | 'error';

export interface UploadedFileAsset {
  assetId: string;
  mimeType: string;
  fileName: string;
  size: number;
}

export interface ProjectUploadState {
  files: File[];
  uploadStatus: ProjectUploadStatus;
  projectId: string | null;
  error: string | null;
  uploadedFileAssets: UploadedFileAsset[];
}

type AnalyzeDocumentsResult =
  | { outcome: 'empty' }
  | { outcome: 'ok'; prefill: ProjectPrefill | null }
  | { outcome: 'aborted' }
  | { outcome: 'error' };

/**
 * Sentinel error thrown by `startUploadFlow` when every per-file outcome
 * was a user cancel (no real failures). Distinct from a normal Error so
 * the wizard's `goNext` can catch it WITHOUT surfacing a toast — the
 * user pressed cancel; a "canceled" toast is hostile feedback for an
 * intentional action.
 *
 * Re-throwing `failures[0]` blindly is not safe: order of `failures`
 * across concurrent rejected branches is non-deterministic, so a real 500
 * racing a user cancel could surface as "canceled" — burying the real
 * error. And in an all-cancel scenario the re-thrown CanceledError read
 * to the wizard as a real failure, toasting "canceled" and refusing to
 * advance.
 *
 * The store now partitions outcomes:
 *   - any realFailure → throw the first realFailure (real errors win)
 *   - only cancels → throw UploadCancelledError, uploadStatus='idle',
 *     error=null. The wizard catches and silently does nothing.
 *   - all success → uploadStatus='done'
 */
export class UploadCancelledError extends Error {
  constructor() {
    // eslint-disable-next-line i18next/no-literal-string -- sentinel message; the wizard catches by instance and returns silently
    super('Upload cancelled by user');
    this.name = 'UploadCancelledError';
    // Preserve prototype chain across transpilation targets so
    // `instanceof UploadCancelledError` works in consumer code.
    Object.setPrototypeOf(this, UploadCancelledError.prototype);
  }
}

interface ProjectUploadActions {
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  clearFiles: () => void;
  ensureDraftProject: () => Promise<string>;
  startUploadFlow: () => Promise<string>;
  analyzeDocuments: () => Promise<AnalyzeDocumentsResult>;
  finishUpload: () => void;
  cancelAnalysis: () => void;
  reset: () => void;
}

export type ProjectUploadStore = ProjectUploadState & ProjectUploadActions;
